'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { CaretRight } from 'phosphor-icons';
import { useState, type JSX } from 'react';
import { c } from '../../../utils/tailwind-merge';
import type {
  TreeAccordionItemProps,
  TreeAccordionProps,
} from './TreeAccordion.types';

const TREE_ITEM_BASE_STYLES = c(
  'flex w-full items-center justify-between py-2 px-1 gap-3',
  'transition-colors duration-150',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-hover'
);

const TREE_ITEM_CONTENT_STYLES =
  'flex min-w-0 items-center gap-3 overflow-hidden';

const INDENT_PX = 24;
const LEAF_OFFSET_PX = 28;

function getSelectedStyles(isSelected: boolean): {
  text: string;
  count: string;
} {
  if (isSelected) {
    return {
      text: 'text-base font-medium text-[#141414]',
      count: 'text-grey-500',
    };
  }
  return { text: 'text-base text-grey-800', count: 'text-grey-500' };
}

type TreeItemCountProps = {
  count: number;
  isSelected: boolean;
};

function TreeItemCount({ count, isSelected }: TreeItemCountProps): JSX.Element {
  const styles = getSelectedStyles(isSelected);
  return (
    <span
      className={c(
        'flex shrink-0 items-center justify-start gap-2 overflow-hidden rounded bg-grey-100 px-1 text-xs',
        styles.count
      )}
    >
      {count.toLocaleString()}
    </span>
  );
}

function TreeAccordionItem<T = unknown>({
  item,
  level = 0,
  selectedId,
  onSelect,
  className,
}: TreeAccordionItemProps<T>): JSX.Element {
  const hasChildren = item.children && item.children.length > 0;
  const isSelected = selectedId === item.id;
  const isRootLevel = level === 0;
  const basePadding = level * INDENT_PX + 4; // Start with 4px padding for the first level to align with the accordion trigger
  const styles = getSelectedStyles(isSelected);
  const isDisabled = item.disabled ?? false;

  const showIcon = item.icon && isRootLevel;

  if (!hasChildren) {
    const leafPadding = basePadding + (isRootLevel ? 0 : LEAF_OFFSET_PX);

    return (
      <button
        data-slot="tree-accordion-item"
        data-test="tree-accordion-item"
        onClick={() => !isDisabled && onSelect?.(item.id)}
        disabled={isDisabled}
        className={c(
          TREE_ITEM_BASE_STYLES,
          'rounded-lg',
          isSelected && 'bg-grey-100',
          isDisabled && 'cursor-not-allowed opacity-50',
          !isDisabled && 'hover:bg-grey-100',
          className
        )}
        style={{ paddingLeft: `${leafPadding}px` }}
        role="treeitem"
        aria-level={level + 1}
        aria-selected={isSelected}
        aria-disabled={isDisabled}
      >
        <div className={TREE_ITEM_CONTENT_STYLES}>
          {showIcon && item.icon}
          <span className={c('truncate', styles.text)}>{item.label}</span>
        </div>
        {item.count !== undefined && (
          <TreeItemCount count={item.count} isSelected={isSelected} />
        )}
      </button>
    );
  }

  return (
    <AccordionPrimitive.Item
      data-slot="tree-accordion-item"
      data-test="tree-accordion-item"
      value={item.id}
      className={c('flex flex-col', className)}
      disabled={isDisabled}
    >
      <AccordionPrimitive.Header className="flex">
        <div
          className={c(
            'group flex w-full',
            TREE_ITEM_BASE_STYLES,
            'rounded-lg',
            isSelected && 'bg-grey-100',
            isDisabled && 'cursor-not-allowed opacity-50',
            !isDisabled && 'hover:bg-grey-100'
          )}
          style={{ paddingLeft: `${basePadding}px` }}
        >
          <AccordionPrimitive.Trigger
            data-test="tree-accordion-caret"
            disabled={isDisabled}
            className={c(
              'group/caret flex shrink-0 items-center justify-center',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-hover'
            )}
          >
            <CaretRight
              size={16}
              className="shrink-0 text-grey-600 transition-transform duration-200 group-data-[state=open]/caret:rotate-90"
            />
          </AccordionPrimitive.Trigger>

          <button
            type="button"
            disabled={isDisabled}
            onClick={(e) => {
              e.preventDefault();
              if (!isDisabled) {
                onSelect?.(item.id);
              }
            }}
            className={c(
              'flex min-w-0 flex-1 items-center justify-between gap-2 overflow-hidden',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-hover'
            )}
            role="treeitem"
            aria-level={level + 1}
            aria-selected={isSelected}
            aria-disabled={isDisabled}
          >
            <div className={TREE_ITEM_CONTENT_STYLES}>
              {showIcon && item.icon}
              <span className={c('truncate', styles.text)}>{item.label}</span>
            </div>
            {item.count !== undefined && (
              <TreeItemCount count={item.count} isSelected={isSelected} />
            )}
          </button>
        </div>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
        <div className="flex flex-col">
          {item.children?.map((child) => (
            <TreeAccordionItem
              key={child.id}
              item={child}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </div>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
}

function TreeAccordion<T = unknown>({
  items,
  selectedId,
  expandedIds: controlledExpandedIds,
  defaultExpandedIds = [],
  onSelect,
  onExpandedChange,
  className,
}: TreeAccordionProps<T>): JSX.Element {
  const [internalExpandedIds, setInternalExpandedIds] =
    useState<string[]>(defaultExpandedIds);

  // Use controlled value if provided, otherwise use internal state
  const isControlled = controlledExpandedIds !== undefined;
  const expandedIds = isControlled
    ? controlledExpandedIds
    : internalExpandedIds;

  function handleExpandedChange(newExpandedIds: string[]): void {
    if (!isControlled) {
      setInternalExpandedIds(newExpandedIds);
    }
    onExpandedChange?.(newExpandedIds);
  }

  return (
    <AccordionPrimitive.Root
      data-slot="tree-accordion"
      type="multiple"
      value={expandedIds}
      onValueChange={handleExpandedChange}
      className={c('flex w-full flex-col overflow-hidden', className)}
      role="tree"
    >
      {items.map((item) => (
        <TreeAccordionItem
          key={item.id}
          item={item}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
    </AccordionPrimitive.Root>
  );
}

export { TreeAccordion, TreeAccordionItem };
